import { Button, Input } from '@breetometer/components';
import { CopyIcon, QrIcon } from 'BreetAssets';
import { Dropdown, NotificationBanner } from 'BreetComponents';
import { alwaysRefetchApiQueryOptions, copyToClipboard, type IDropDownItem, type ISellAssets } from 'BreetHelpers';
import { useModalApi } from 'BreetHooks';
import { selectFiatMeta, useAppSelector, useGetSellAssetsQuery } from 'BreetRedux';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';

import { filterAssetItem, filterAssetItemSymbol, sortTradeAssets } from '../../DialogFundRequest.helpers';
import DropdownItemAssets from './DropdownItemAssets';

const STABLE_SYMBOLS = ['USDT', 'USDC'] as const;
type StableSymbol = (typeof STABLE_SYMBOLS)[number];
const isStableSymbol = (value?: string): value is StableSymbol => STABLE_SYMBOLS.includes(value as StableSymbol);

const toDropdownItem = (asset: ISellAssets): IDropDownItem => ({
	code: asset.name,
	icon: asset.avatar,
	name: asset.identifier,
	value: asset.name,
});

const CryptoFundingMethod = () => {
	const fiatMetaProps = useAppSelector(selectFiatMeta);

	const [topSelection, setTopSelection] = useState<IDropDownItem | undefined>();
	const [coin, setCoin] = useState<IDropDownItem | undefined>();

	const { handleOpenModal } = useModalApi();

	const { data: tradeSellAssetsData } = useGetSellAssetsQuery(undefined, alwaysRefetchApiQueryOptions);

	const topValue = topSelection?.value;
	const stableGroup = isStableSymbol(topValue) ? topValue : undefined;

	const sellAssetItems = useMemo(() => {
		const rawAssets = tradeSellAssetsData?.data ?? [];

		const regular = [...rawAssets]
			.sort(sortTradeAssets)
			.filter(filterAssetItem)
			.filter(filterAssetItemSymbol('USDC'))
			.filter(filterAssetItemSymbol('USDT'))
			.map(toDropdownItem);

		const stableOptions = STABLE_SYMBOLS.reduce<IDropDownItem[]>((acc, symbol) => {
			const variant = rawAssets.find((i) => i.symbol === symbol);
			if (variant) acc.push({ code: symbol, name: symbol, value: symbol, icon: variant.avatar });
			return acc;
		}, []);

		return [...regular, ...stableOptions];
	}, [tradeSellAssetsData?.data]);

	const stableVariants = useMemo(() => {
		if (!stableGroup) return [];

		return [...(tradeSellAssetsData?.data ?? [])]
			.sort(sortTradeAssets)
			.filter(filterAssetItem)
			.filter((i) => i.symbol === stableGroup)
			.map(toDropdownItem);
	}, [tradeSellAssetsData?.data, stableGroup]);

	const handleItemSelect = useCallback(
		(item: IDropDownItem) => (e: MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			setTopSelection(item);
			setCoin(isStableSymbol(item.value) ? undefined : item);
		},
		[]
	);

	const handleVariantSelect = useCallback(
		(item: IDropDownItem) => (e: MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			setCoin(item);
		},
		[]
	);

	const walletAddress = useMemo(
		() => tradeSellAssetsData?.data?.find((i) => i.name === coin?.value)?.wallet?.address ?? '',
		[tradeSellAssetsData?.data, coin]
	);

	return (
		<div className='new-drop-down-input fund-wallet-input crypto-fund-method'>
			<Dropdown.Modern
				items={sellAssetItems}
				label='Select Coin'
				searchItemPlaceholder='Search asset'
				id='select-funding-method'
				selectedItem={topSelection}
				CustomItemOption={DropdownItemAssets}
				handleItemSelect={handleItemSelect}
				hideSearch={false}
			/>

			{!!stableGroup && (
				<Dropdown.Modern
					items={stableVariants}
					label={`Select ${stableGroup}`}
					id='select-stablecoin-variant'
					selectedItem={coin}
					CustomItemOption={DropdownItemAssets}
					handleItemSelect={handleVariantSelect}
					hideSearch={false}
				/>
			)}

			{!!coin?.value && (
				<>
					<div className='wallet-address-value'>
						<Input
							label='Wallet Address'
							id='addresses'
							widthType='full'
							placeholder=' '
							maxLength={128}
							value={walletAddress}
						/>

						<div className='icon-wrapper'>
							<Button
								trailingIcon={
									<CopyIcon
										variant='plain'
										className='copy-icon svg-dark'
									/>
								}
								onClick={copyToClipboard(walletAddress, 'Wallet address copied')}
								className='copy-btn theme-background-none bg-tertiary-400-hover color-neutral-50-hover tertiary-semi-hover'
							>
								Copy Address
							</Button>
							<Button
								trailingIcon={<QrIcon className='qr-icon svg-dark' />}
								onClick={handleOpenModal({
									id: 'qrCode',
									metadata: { url: walletAddress, download: true, position: 'right-middle' },
									hideCloseButton: true,
								})}
								className='qrcode-btn bg-primary-400 color-neutral-50 border-dark-navy color-tertiary-400 bg-primary-500-hover color-neutral-50-hover outline-hover'
							>
								View QR Code
							</Button>
						</div>
					</div>

					<div className='payment-request-form_notificationbanner'>
						<NotificationBanner
							message={`Any crypto sent to this address will be deposited to your USD/${fiatMetaProps.currency} Wallet.`}
							status='warning'
							type='standard'
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default CryptoFundingMethod;
